import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { getRoleBadgeVariant, getRoleLabel } from "@/lib/utils";
import { AlertTriangle, LogOut, UserMinus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Role } from "../../gen/rbt/cloud/v1alpha1/organization/organization_pb";
import { useOrganization } from "../../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";

interface OrganizationMembersSectionProps {
  organizationId: string;
  currentUserId: string;
}

export function OrganizationMembersSection({
  organizationId,
  currentUserId,
}: OrganizationMembersSectionProps) {
  const { useGetMembers, updateMember, removeMemberFromOrgAndUser } =
    useOrganization({
      id: organizationId,
    });
  const { response, aborted } = useGetMembers({}, { suspense: true });

  if (aborted || !response) {
    return null;
  }
  const members = response.members;

  const { toast } = useToast();
  const navigate = useNavigate();
  const [showLeaveConfirmDialog, setShowLeaveConfirmDialog] = useState(false);
  const [showOnlyOwnerDialog, setShowOnlyOwnerDialog] = useState(false);

  const currentUserRole = members[currentUserId]?.role;
  const isOwner = currentUserRole === Role.OWNER;

  // Count how many owners exist in the organization.
  const ownerCount = Object.values(members).filter(
    (member) => member.role === Role.OWNER
  ).length;
  const isOnlyOwner = isOwner && ownerCount === 1;

  const handleRoleChange = async (userId: string, newRole: Role) => {
    // Users cannot change their own role.
    if (userId === currentUserId) {
      return;
    }

    const { aborted } = await updateMember({ userId, role: newRole });

    if (aborted) {
      toast({
        title: "Error",
        description: aborted.message || "Failed to update member role.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Role Updated",
      description: `Member's role has been changed to ${getRoleLabel(
        newRole
      )}.`,
    });
  };

  const handleLeaveClick = () => {
    if (isOnlyOwner) {
      setShowOnlyOwnerDialog(true);
      return;
    }
    setShowLeaveConfirmDialog(true);
  };

  const handleConfirmLeave = async () => {
    setShowLeaveConfirmDialog(false);

    const { aborted } = await removeMemberFromOrgAndUser({
      userId: currentUserId,
    });

    if (aborted) {
      toast({
        title: "Error",
        description: aborted.message || "Failed to leave organization.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Left Organization",
      description: "You have left the organization.",
    });

    // Navigate away since user is no longer in this org.
    navigate("/");
  };

  const handleRemoveMember = async (userId: string) => {
    const { aborted } = await removeMemberFromOrgAndUser({ userId });

    if (aborted) {
      toast({
        title: "Error",
        description: aborted.message || "Failed to remove member.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Member Removed",
      description: "Member has been removed from the organization.",
    });
  };

  const memberEntries = Object.entries(members);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Members</CardTitle>
        {isOwner && (
          <CardDescription>
            Manage members and their roles in your organization.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {memberEntries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No members in this organization.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">
                  {isOwner ? "Actions" : "Leave"}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {memberEntries.map(([userId, member]) => (
                <TableRow key={userId}>
                  <TableCell>
                    <div className="font-medium text-sm">
                      {member.email}
                      {userId === currentUserId && (
                        <span className="ml-2 text-muted-foreground">
                          (you)
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {isOwner && userId !== currentUserId ? (
                      <Select
                        value={member.role.toString()}
                        onValueChange={(value) =>
                          handleRoleChange(userId, parseInt(value) as Role)
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={Role.OWNER.toString()}>
                            Owner
                          </SelectItem>
                          <SelectItem value={Role.EDITOR.toString()}>
                            Editor
                          </SelectItem>
                          <SelectItem value={Role.VIEWER.toString()}>
                            Viewer
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge
                        variant={getRoleBadgeVariant(member.role)}
                        className="capitalize"
                      >
                        {getRoleLabel(member.role)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {userId === currentUserId ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLeaveClick}
                        className="bg-background text-destructive hover:text-destructive hover:bg-background"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Leave
                      </Button>
                    ) : isOwner ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(userId)}
                        className="bg-background text-destructive hover:text-destructive hover:bg-background"
                      >
                        <UserMinus className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Confirmation dialog for leaving organization. */}
      <Dialog
        open={showLeaveConfirmDialog}
        onOpenChange={setShowLeaveConfirmDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Leave Organization
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to leave this organization? You will lose
              access to all organization resources.
              {isOwner && (
                <>
                  <br />
                  <br />
                  <strong>Note:</strong> You are an owner. After leaving, you
                  will no longer be able to manage this organization.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLeaveConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmLeave}>
              Leave Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog for when the only owner tries to leave. */}
      <Dialog open={showOnlyOwnerDialog} onOpenChange={setShowOnlyOwnerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Cannot Leave Organization
            </DialogTitle>
            <DialogDescription>
              You are the only owner of this organization. Every organization
              must have at least one owner.
              <br />
              <br />
              To leave, first assign another member as an owner.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowOnlyOwnerDialog(false)}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
